"use client";

import { Download, Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const playbookItems = [{
  index: "01",
  title: "OTA Revenue Optimization",
  steps: ["Audit your current OTA listings for content quality and visual appeal.", "Implement dynamic rate management to capture high-intent travelers.", "Leverage OTA-specific promotional tools to boost visibility.", "Analyze competitor pricing to maintain a competitive edge.", "Regularly update availability to prevent overbooking and parity issues."]
}, {
  index: "02",
  title: "Hospitality Tech Stack",
  steps: ["Evaluate your current PMS for integration capabilities.", "Select a robust Channel Manager to sync inventory in real-time.", "Implement a Booking Engine that prioritizes direct conversions.", "Utilize Revenue Management Systems (RMS) for data-driven forecasting.", "Ensure all tech solutions are mobile-responsive and user-friendly."]
}, {
  index: "03",
  title: "Revenue Audit Process",
  steps: ["Review historical booking data to identify demand patterns.", "Assess current rate parity across all distribution channels.", "Identify missed revenue opportunities in your current strategy.", "Analyze guest feedback to improve service-based pricing.", "Receive a comprehensive report with actionable growth recommendations."]
}, {
  index: "04",
  title: "Direct Booking Growth",
  steps: ["Optimize your website for high-conversion user journeys.", "Implement loyalty programs to encourage repeat direct bookings.", "Use targeted digital marketing to drive traffic to your booking engine.", "Offer exclusive perks for guests who book directly.", "Monitor conversion rates to refine your direct booking strategy."]
}, {
  index: "05",
  title: "Rate Parity Management",
  steps: ["Monitor rates across all OTAs and your direct website daily.", "Identify and resolve discrepancies with channel partners.", "Ensure your direct channel always offers the best value.", "Use automated tools to track parity in real-time.", "Maintain brand integrity through consistent pricing."]
}, {
  index: "06",
  title: "Dynamic Pricing Strategy",
  steps: ["Adjust rates based on real-time market demand and occupancy.", "Set pricing tiers for different guest segments.", "Analyze seasonal trends to maximize RevPAR.", "Automate rate updates to respond to market shifts instantly.", "Balance occupancy and ADR for optimal revenue performance."]
}, {
  index: "07",
  title: "Consultation Preparation",
  steps: ["Gather your last 12 months of occupancy and ADR data.", "List your current distribution channels and tech partners.", "Define your primary revenue goals for the next quarter.", "Prepare a list of challenges you face in the current market.", "Schedule your initial discovery call with Sunil Singh."]
}];
export default function RevenueStrategy() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="1275" id="revenuestrategyfaq" className="bg-background text-foreground relative py-32 md:py-44 overflow-hidden" style={{
    clipPath: "polygon(0 7%, 100% 0, 100% 100%, 0 100%)"
  }}>
      <div ref={ref} className={`container mx-auto px-6 md:px-12 max-w-[1400px] transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-32">
              {}
              <div className="flex items-center gap-3 mb-8">
                <span className="block w-6 h-px bg-secondary"></span>
                <span className="font-default text-[0.75rem] tracking-[0.18em] uppercase text-muted-foreground">
                  Revenue Strategy 
                </span>
              </div>

              {}
              <h2 className="font-default uppercase text-foreground text-[clamp(2.5rem,6vw,5.5rem)] leading-[0.92] tracking-[-0.005em] mb-8">
                The{" "}
                <em className="not-italic">
                  <span className="italic text-secondary">PLAYBOOK</span>
                </em>
                .
              </h2>

              {}
              <p className="font-default text-[clamp(1.0625rem,1.2vw,1.25rem)] leading-[1.6] text-foreground/70 max-w-md">
                Expert revenue management, tech implementation, and audit strategies to maximize your hotel's profitability.
              </p>

              {}
              <div className="hidden lg:block mt-12 font-default text-[0.6875rem] tracking-[0.15em] uppercase text-muted-foreground">
                HotelRevBaBa / Revenue Excellence
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="w-full">
              {playbookItems.map((item, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-b border-foreground/20 last:border-b-0 group">
                  <AccordionTrigger className="hover:no-underline py-0 [&>svg]:hidden">
                    <div className="flex items-center justify-between w-full min-h-[96px] py-6 gap-6 group-hover:pl-2 transition-all duration-200">
                      <div className="flex items-center gap-6 md:gap-10 flex-1">
                        <span className="font-default text-sm md:text-base text-secondary tracking-[0.1em] shrink-0">
                          {item.index} 
                        </span>
                        <h3 className="font-default uppercase text-foreground text-[clamp(1.25rem,2.4vw,2.25rem)] leading-[1] tracking-[0] text-left transition-transform duration-200 group-hover:translate-x-2">
                          {item.title}
                        </h3>
                      </div>
                      <span className="shrink-0 text-secondary transition-transform duration-300 group-data-[state=open]:rotate-45">
                        <Plus className="size-7 md:size-9" strokeWidth={1.75} />
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-10 pl-0 md:pl-[5.5rem]">
                    <ol className="space-y-3 mb-8 max-w-2xl">
                      {item.steps.map((step, sIdx) => <li key={sIdx} data-index={sIdx} className="flex gap-4 font-default text-base leading-[1.65] text-foreground/80">
                          <span className="font-default text-sm text-secondary shrink-0 pt-1 min-w-[2rem]">
                            {String(sIdx + 1).padStart(2, "0")}.
                          </span>
                          <span>{step}</span>
                        </li>)}
                    </ol>
                    <Link to="/ContactPage" className="inline-flex items-center gap-2 font-default text-[0.75rem] tracking-[0.18em] uppercase text-foreground hover:text-secondary transition-colors duration-200 underline underline-offset-[6px] decoration-1">
                      <Download className="size-3.5" strokeWidth={1.75} />
                      Download Strategy PDF →
                    </Link>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}